import { create } from 'zustand';

import {
  feimaChatUserInfo,
  feimaChatUserLoginMp,
  feimaChatUserLoginMpQuery,
} from '@/api/userApi';
import {
  getStorageUser,
  removeStorageUser,
  setStorageUser,
} from '@/utils/storage';
import { feimaChatBotSwitchList, feimaChatBotSwitch } from '@/api/botApi';
import { feimaChatBotCheckDefaultAgent } from '@/api/agentAvatarApi';

export interface UserInfo {
  id?: string;
  nickname?: string;
  token?: string;
  avatar?: string;
  unionId?: string;
  openId?: string;
  shopId?: string;
  /** user/info 返回的当前账户激活智能体 ID */
  currBotId?: string;
  [key: string]: any;
}

/** 可切换智能体列表条目（来自 /bot/switchList） */
export type BotListItem = API.FeimaChatBotSwitchListResponse['data']['list'][0];

interface useStore {
  userInfo: UserInfo;
  setUserInfo: (userInfo: UserInfo) => void;
  login: (sceneId: string) => Promise<void>;
  updateUserInfo: () => Promise<UserInfo>;
  loginCode: (code: string, inviterUserId: string) => Promise<void>;
  inviterUserId: string;
  updateInviterUserId: (inviterUserId: string) => void;
  sharBotId: string;
  updateShareBotId: (sharBotId: string) => void;
  removeUserInfo: () => Promise<void>;
  triggerLogin: number;
  setTriggerLogin: () => void;
  mobileLogin: number;
  setMobileLogin: () => void;
  /** 全局可切换智能体列表（提升自 useSwitchAgent，避免多处重复请求） */
  botList: BotListItem[];
  /** botList 是否已完成首次加载+校验 */
  botListLoaded: boolean;
  /** botList 正在请求中 */
  botListLoading: boolean;
  /** 拉取 /bot/switchList 并写入 store */
  loadBotList: () => Promise<BotListItem[]>;
  /** 全局控制创建分身弹窗（AgentAvatarModal）的显示，供任意组件跨层触发 */
  agentAvatarModalOpen: boolean;
  setAgentAvatarModalOpen: (open: boolean) => void;
  /** 全局控制无默认分身引导弹窗（GuideCreateAgentModal）的显示 */
  guideCreateAgentModalOpen: boolean;
  setGuideCreateAgentModalOpen: (open: boolean) => void;
  /** 全局唯一智能体介绍 Popover，存入 trigger 元素供 floating-ui 定位 */
  agentInfoPopover: {
    open: boolean;
    botId: string;
    anchorEl: HTMLElement | null;
    showChatBtn: boolean;
  };
  openAgentInfoPopover: (botId: string, anchorEl: HTMLElement, options?:{ showChatBtn: boolean}) => void;
  closeAgentInfoPopover: () => void;
  /**
   * 路由初始化校验：
   * 1. 拉取 botList
   * 2. 若 currBotId 无效（不存在或不在列表中），找 botType=3 的分身 bot 无感切换
   * 3. 标记 botListLoaded=true
   */
  initActiveBot: () => Promise<void>;
}

const user = getStorageUser();

export const userStore = create<useStore>((set, get) => ({
  userInfo: user as UserInfo,
  inviterUserId: '',
  sharBotId: '',
  triggerLogin: -1,
  mobileLogin: -1,
  botList: [],
  botListLoaded: false,
  botListLoading: false,
  agentAvatarModalOpen: false,
  guideCreateAgentModalOpen: false,
  agentInfoPopover: {
    open: false,
    botId: '',
    anchorEl: null,
    showChatBtn: true,
  },

  // 登录
  login: (sceneId: string) =>
    new Promise<void>((resolve, reject) => {
      feimaChatUserLoginMpQuery({ sceneId })
        .then(result => {
          if (result?.token) {
            set({ userInfo: result });
            setStorageUser(result);
            resolve(result as any);
          }
        })
        .catch(() => {
          reject();
        });
    }),

  // 更新用户信息
  updateUserInfo: () =>
    new Promise<UserInfo>((resolve, reject) => {
      feimaChatUserInfo()
        .then(result => {
          set({ userInfo: result });
          setStorageUser(result);
          resolve(result);
        })
        .catch(() => {
          reject();
        });
    }),

  // 设置用户信息
  setUserInfo: (userInfo: UserInfo) => {
    set({ userInfo });
    setStorageUser(userInfo);
  },

  // 获取code登录
  loginCode: (code: string, inviterUserId: string) =>
    new Promise<void>((resolve, reject) => {
      feimaChatUserLoginMp({ code, inviterUserId })
        .then(result => {
          if (result?.token) {
            set({ userInfo: result });
            setStorageUser(result);
            resolve();
          } else {
            reject();
          }
        })
        .catch(() => {
          reject();
        });
    }),

  updateInviterUserId: (inviterUserId: string) => {
    set({ inviterUserId });
  },

  updateShareBotId: (sharBotId: string) => {
    set({ sharBotId });
  },

  setTriggerLogin: () =>
    set(state => ({ triggerLogin: state.triggerLogin + 1 })),

  setMobileLogin: () => set(state => ({ mobileLogin: state.mobileLogin + 1 })),

  removeUserInfo: () =>
    new Promise<void>((resolve, reject) => {
      try {
        set({ userInfo: {} });
        removeStorageUser();
        resolve();
      } catch (error) {
        reject();
      }
    }),

  // 拉取可切换列表并存入全局 store
  loadBotList: async () => {
    set({ botListLoading: true });
    try {
      const res = await feimaChatBotSwitchList(undefined);
      const list = res.list || [];
      set({ botList: list });
      return list;
    } finally {
      set({ botListLoading: false });
    }
  },

  setAgentAvatarModalOpen: (open: boolean) =>
    set({ agentAvatarModalOpen: open }),

  setGuideCreateAgentModalOpen: (open: boolean) =>
    set({ guideCreateAgentModalOpen: open }),

  openAgentInfoPopover: (
    botId,
    anchorEl,
    options?: { showChatBtn: boolean }
  ) =>
    set({
      agentInfoPopover: { open: true, botId, anchorEl, showChatBtn: typeof options?.showChatBtn === 'undefined' ? true : options.showChatBtn  },
    }),

  closeAgentInfoPopover: () =>
    set(state => ({
      agentInfoPopover: { ...state.agentInfoPopover, open: false },
    })),

  // 路由初始化：校验 currBotId，必要时无感切换到 botType=3 的分身 bot
  initActiveBot: async () => {
    // 防止重复执行
    if (get().botListLoaded) return;

    try {
      // 1. 拉取可切换列表
      const list = await get().loadBotList();
      const currBotId = get().userInfo?.currBotId;

      // 2. 校验 currBotId 是否有效（存在且在列表中）
      const isValid = Boolean(currBotId && list.some(b => b.id === currBotId));
      if (isValid) {
        set({ botListLoaded: true });
        return;
      }

      // 3. currBotId 无效，从 avatar/list 找 botType=3 的分身 bot
      const avatarRes = await feimaChatBotCheckDefaultAgent({
        page: 0,
        size: 20,
        isLimit: true,
      });
      const avatarList = avatarRes?.list ?? [];
      // 优先 botType=3，否则取第一个
      const fallback =
        avatarList.find((b: any) => b.botType === 3) ?? avatarList[0];
      const targetBotId: string | undefined = fallback?.botId;

      if (!targetBotId) {
        // 没有可用 bot，直接标记完成
        set({ botListLoaded: true });
        return;
      }

      // 4. 无感调用切换接口
      await feimaChatBotSwitch({ botId: targetBotId });

      // 5. 同步更新 store 和 localStorage 中的 currBotId
      const newUserInfo = { ...get().userInfo, currBotId: targetBotId };
      set({ userInfo: newUserInfo });
      setStorageUser(newUserInfo);
    } catch {
      // 初始化失败不阻塞渲染
    } finally {
      set({ botListLoaded: true });
    }
  },
}));
